import React, { useCallback, useEffect, useState } from 'react';
import { BellRing, ChevronDown, ChevronUp, Loader2, Phone, Send, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';

/**
 * Price reduction buyer alert for one listing (CG, Monday 13458776745).
 *
 * When an agent lowers the price, the CRM records the reduction and, once
 * alerts are live, emails the buyers who enquired, attended, saved it or now
 * strongly match it, after a 30 minute hold. This panel is where the agent
 * sees that alert: when it will go, why it is waiting, cancel / send now /
 * confirm, and every buyer it concerns with their match and intent scores,
 * including the casual viewers and weaker matches who are not emailed but are
 * worth a call.
 *
 * Renders nothing when the listing has no recent reduction.
 */

const RECENT_DAYS = 14;
const ACTIVE = ['recorded', 'held', 'needs_confirmation', 'sending'];

const TIER_LABEL = {
  interacted: 'Enquired / attended / saved',
  strong: 'Strong match',
  follow_up: 'Call, not emailed',
};

const EMAIL_STATUS = {
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Emailed',
  excluded: 'Left out',
  skipped: 'Left out',
  cancelled: 'Cancelled',
  failed: 'Failed',
  unknown: 'Unconfirmed',
  not_emailed: 'Not emailed',
};

// Why someone was left out, in the agent's words (the pre-send check codes).
const LEFT_OUT_REASON = {
  listing_changed: 'listing changed',
  invalid_address: 'no valid email',
  duplicate_address: 'duplicate',
  already_sent: 'already sent',
  in_active_run: 'in another send',
  vendor: 'the vendor',
  recent_price_alert: 'had a price alert this week',
  archived: 'archived',
  opted_out: 'unsubscribed',
  blocked_address: 'unsubscribed or bounced',
  price_alerts_off: 'price alerts off',
  not_looking: 'purchased or paused',
  no_record: 'not in the CRM',
  unengaged: 'not opening emails',
};

export const emailLabel = (p) => {
  if (['excluded', 'skipped'].includes(p.email_status)) {
    const why = LEFT_OUT_REASON[p.email_reason];
    return why ? `Left out: ${why}` : 'Left out';
  }
  return EMAIL_STATUS[p.email_status] || (p.email_status ? p.email_status : 'Not sent yet');
};

export const money = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `$${Math.round(n).toLocaleString('en-AU')}` : '';
};

const clock = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

const isRecent = (event) =>
  ACTIVE.includes(event.status)
  || Date.now() - new Date(event.created_at).getTime() < RECENT_DAYS * 24 * 3600 * 1000;

/** The one line that says what is happening with this alert. */
export function headline(event, mode) {
  const run = event.send_run;
  const n = run?.recipients ?? 0;
  switch (event.status) {
    case 'held':
      if (run?.status === 'pending_approval') return `Waiting for a super admin to approve the email to ${n} buyers.`;
      if (run?.send_after && new Date(run.send_after).getTime() <= Date.now()) return `Emailing ${n} buyers shortly.`;
      return `${n} buyers will be emailed at ${clock(run?.send_after)} unless you cancel.`;
    case 'needs_confirmation':
      return `Waiting for your OK before ${n} buyers are emailed.`;
    case 'sending':
      return `Emailing ${n} buyers now.`;
    case 'sent':
      return `Emailed ${n} buyers.`;
    case 'below_threshold':
      return 'Under 1% and $10,000, so no alert was sent.';
    case 'no_audience':
      return 'Nobody was eligible for an email.';
    case 'cancelled':
      return 'Alert cancelled.';
    case 'superseded':
      return 'Replaced by a later price change.';
    case 'recorded':
      return mode === 'live' ? 'Working out who to tell...' : 'Recorded. Alerts are not switched on yet, so nothing was sent.';
    default:
      return event.status_label || event.status;
  }
}

export default function PriceReductionAlerts({ propertyId, priceKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const res = await api.get('/notifications/price-reductions/', { params: { property_id: propertyId } });
      setData(res.data);
    } catch {
      setData(null); // no access or older backend: show nothing
    }
  }, [propertyId]);

  useEffect(() => {
    load();
  }, [load, priceKey]);

  const latest = (data?.results || []).find(isRecent);

  // The audience is worked out in the background after a save: check back
  // while the alert is still being prepared or held.
  useEffect(() => {
    if (!latest || !['recorded', 'held', 'sending'].includes(latest.status)) return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [latest, load]);

  const loadDetail = useCallback(async (id) => {
    setLoadingDetail(true);
    try {
      const res = await api.get(`/notifications/price-reductions/${id}/`);
      setDetail(res.data);
    } catch {
      toast.error('Could not load the buyers for this alert');
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  if (!latest) return null;

  const act = async (action, success) => {
    setBusy(action);
    try {
      await api.post(`/notifications/price-reductions/${latest.id}/${action}/`);
      toast.success(success);
      await load();
      if (open) loadDetail(latest.id);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'That did not work. Reload and try again.');
    } finally {
      setBusy('');
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && (!detail || detail.id !== latest.id)) loadDetail(latest.id);
  };

  const counts = latest.audience_counts || {};
  const run = latest.send_run;
  // Send now only while there is still a hold to skip.
  const waiting = latest.status === 'held' && run?.status === 'queued'
    && !!run?.send_after && new Date(run.send_after).getTime() > Date.now();

  return (
    <div data-testid="price-reduction-alerts" className="mt-4 mb-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <BellRing className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Price reduced {money(latest.old_price)} to {money(latest.new_price)}
              <span className="ml-2 font-normal text-gray-600">({latest.drop_percent}%)</span>
            </p>
            <p className="text-sm text-gray-700">{headline(latest, data?.mode)}</p>
            {latest.status === 'needs_confirmation' && (latest.confirm_reasons || []).map((r) => (
              <p key={r} className="text-xs text-amber-800">{r}</p>
            ))}
            {(counts.interacted || counts.strong || counts.follow_up) ? (
              <p className="mt-1 text-xs text-gray-600">
                {counts.interacted || 0} enquired, attended or saved · {counts.strong || 0} strong matches
                {counts.newly_qualified ? ` (${counts.newly_qualified} now in budget)` : ''}
                {' · '}{counts.follow_up || 0} worth a call
              </p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {latest.status === 'needs_confirmation' && (
            <button type="button" disabled={!!busy} onClick={() => act('confirm', 'Alert confirmed')}
              className="inline-flex items-center gap-1 rounded-md bg-[#0a3d62] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
              {busy === 'confirm' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send it
            </button>
          )}
          {waiting && (
            <button type="button" disabled={!!busy} onClick={() => act('send-now', 'Sending now')}
              className="inline-flex items-center gap-1 rounded-md bg-[#0a3d62] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
              {busy === 'send-now' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send now
            </button>
          )}
          {latest.status === 'below_threshold' && data?.mode === 'live' && (
            <button type="button" disabled={!!busy} onClick={() => act('send-anyway', 'Alert on its way after the hold')}
              className="inline-flex items-center gap-1 rounded-md border border-[#0a3d62] px-3 py-1.5 text-xs font-semibold text-[#0a3d62] disabled:opacity-50">
              {busy === 'send-anyway' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send an alert anyway
            </button>
          )}
          {['held', 'needs_confirmation'].includes(latest.status) && (
            <button type="button" disabled={!!busy} onClick={() => act('cancel', 'Alert cancelled')}
              className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 disabled:opacity-50">
              {busy === 'cancel' ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />} Cancel
            </button>
          )}
          <button type="button" onClick={toggle}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">
            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Buyers
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 overflow-x-auto rounded-lg border border-gray-200 bg-white">
          {loadingDetail && <div className="p-3 text-xs text-gray-500">Loading buyers...</div>}
          {!loadingDetail && detail && (detail.audience || []).length === 0 && (
            <div className="p-3 text-xs text-gray-500">No buyers for this listing yet.</div>
          )}
          {!loadingDetail && detail && (detail.audience || []).length > 0 && (
            <table className="min-w-full text-left text-xs" data-testid="price-reduction-audience">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-3 py-2">Buyer</th>
                  <th className="px-3 py-2">Why</th>
                  <th className="px-3 py-2">Match</th>
                  <th className="px-3 py-2">Intent</th>
                  <th className="px-3 py-2">Email</th>
                </tr>
              </thead>
              <tbody>
                {detail.audience.map((p) => (
                  <tr key={`${p.email}-${p.contact_id}-${p.buyer_id}`} className="border-t border-gray-100">
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{p.name || p.email}</div>
                      <div className="text-gray-500">{TIER_LABEL[p.tier]}</div>
                      {p.phone && (
                        <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 text-[#0a3d62]">
                          <Phone className="h-3 w-3" /> {p.phone}
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{(p.reasons || []).join(', ')}</td>
                    <td className="px-3 py-2">{p.match_score != null ? `${p.match_score}/100` : ''}</td>
                    <td className="px-3 py-2">{p.intent_score != null ? `${p.intent_score}/100` : ''}</td>
                    <td className="px-3 py-2">{emailLabel(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
